import type { GsapTimelineLike } from '../editableVisibility';
import type { LintFinding, LintSeverity } from './types';

/**
 * The loaded composition, for rules that need more than the HTML source — the live DOM and the
 * seekable timeline. `timeline` is null when the composition declares no `window.__timelines.main`.
 */
export interface RuntimeLintContext {
    document: Document;
    timeline: GsapTimelineLike | null;
}

/**
 * The runtime counterpart to the static `LintRule`: it inspects the loaded composition rather than
 * the raw HTML string. The asset runs these after loading the iframe and merges their findings with
 * the static ones into one `LintFinding[]`.
 */
export interface RuntimeLintRule {
    id: string;
    severity: LintSeverity;
    guidance?: string;
    check(context: RuntimeLintContext): string[];
}

// Natural text editing edits at the current frame, so editable nodes need not share one frame; the
// runtime-lint framework stays for future rules that must inspect the loaded composition.
export const CORE_RUNTIME_LINT_RULES: RuntimeLintRule[] = [];

/**
 * Run the runtime rules against a loaded composition. Synchronous (the composition is already
 * loaded); the asset awaits the load, then calls this.
 */
export function runRuntimeLintRules(
    context: RuntimeLintContext,
    rules: RuntimeLintRule[] = CORE_RUNTIME_LINT_RULES,
): LintFinding[] {
    const findings: LintFinding[] = [];
    for (const rule of rules) {
        for (const message of rule.check(context)) {
            findings.push({ ruleId: rule.id, severity: rule.severity, message });
        }
    }
    return findings;
}
